// Leftovers that point at their source meal, and the scaling that follows
// from them (redesign slice 5a; docs/2026-10-03-plan-leftovers-scaling-design.md).
//
// A cooked meal's planned servings are its week's "People served" plus the
// "People served" of every leftovers slot drawing on it with scalesSource
// set. A recipe is scaled up to that, never down. Nothing is stored: it is
// worked out from the plan whenever it's needed, so adding or removing a
// leftovers slot rescales its source with no write.
//
// In memory, week.leftovers marks a placement as leftovers (as before) and
// week.leftoverSources holds the link, both keyed `${day}|${slot}|${dish}`.
// db.js translates to and from week_plan_meals' source_* columns (0053).
import { DAYS, DINING_OUT, MEAL_SLOTS } from "./constants";
import { normalizeDishes } from "./planUtils";
import { toISODate } from "./utils";
import type { Day, DishType, MealSlot, PlansByWeek, Recipe, RecipeById, WeekPlan } from "./types";

export interface MealRef {
  weekStart: string;
  day: Day;
  mealSlot: MealSlot;
  dishType: DishType;
}

export interface LeftoverSource extends MealRef {
  // Whether this leftovers slot counts toward its source's size. False when
  // the link was made after the source's day had passed: a past meal is
  // treated as cooked and is never rescaled (decided 2026-10-03).
  scalesSource: boolean;
}

export const mealKey = (day: string, slot: string, dish: string) => `${day}|${slot}|${dish}`;
const sameMeal = (a: MealRef, b: MealRef) => a.weekStart === b.weekStart && a.day === b.day && a.mealSlot === b.mealSlot && a.dishType === b.dishType;

const DEFAULT_SERVINGS = 4;
export const weekServings = (plansByWeek: PlansByWeek, weekStart: string) => plansByWeek[weekStart]?.servings || DEFAULT_SERVINGS;

// The calendar date of a day in a plan week. A week's start date is a real
// date, so this holds whatever day the household's week starts on.
export function mealDate(weekStart: string, day: Day): Date {
  const start = new Date(`${weekStart}T00:00:00`);
  const startIdx = (start.getDay() + 6) % 7; // Monday = 0
  const offset = (DAYS.indexOf(day) - startIdx + 7) % 7;
  return new Date(start.getFullYear(), start.getMonth(), start.getDate() + offset);
}

const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
const slotOrder = (slot: MealSlot) => MEAL_SLOTS.indexOf(slot);

// ------------------------------------------------------- who draws on a meal ---

export interface Draw {
  ref: MealRef; // the leftovers slot
  scalesSource: boolean;
}

// Every leftovers slot, in any loaded week, that names `source`.
export function drawsOn(plansByWeek: PlansByWeek, source: MealRef): Draw[] {
  const draws: Draw[] = [];
  for (const [weekStart, week] of Object.entries(plansByWeek)) {
    for (const [key, link] of Object.entries(week.leftoverSources || {})) {
      if (!week.leftovers?.[key] || !sameMeal(link, source)) continue;
      const [day, mealSlot, dishType] = key.split("|") as [Day, MealSlot, DishType];
      draws.push({ ref: { weekStart, day, mealSlot, dishType }, scalesSource: link.scalesSource });
    }
  }
  return draws;
}

// People the meal is cooked for: its own week's People served, plus each
// leftovers slot that counts toward it.
export function plannedServings(plansByWeek: PlansByWeek, meal: MealRef): number {
  return drawsOn(plansByWeek, meal).reduce((n, d) => (d.scalesSource ? n + weekServings(plansByWeek, d.ref.weekStart) : n), weekServings(plansByWeek, meal.weekStart));
}

// Scale up to the planned servings, never down. 1 for a recipe with no
// servings, or one that already makes enough.
export function scaleFactorFor(recipe: Pick<Recipe, "servings" | "recipeType"> | undefined, planned: number): number {
  if (!recipe || !(recipe.servings > 0)) return 1;
  if (recipe.recipeType === "preserves" || recipe.recipeType === "medicinal") return 1;
  return planned > recipe.servings ? planned / recipe.servings : 1;
}

// ------------------------------------------------------------ grocery list ---

export interface GroceryPlacement {
  recipeId: string;
  factor: number;
  // Where it is planned, for "which recipes and days need this" (slice 6b):
  // a meal, or one of the week's lists.
  day?: Day;
  slot?: MealSlot;
  list?: "snacks" | "preserves" | "medicinal";
}

// What a week's grocery list is built from: each cooked dish at its scale,
// and the snacks/preserves/medicinal lists as written. Leftovers slots add
// nothing -- their share is bought through their source. Dining out and
// recipes the viewer can't see are skipped later, by recipeById.
export function groceryPlacements(plansByWeek: PlansByWeek, weekStart: string, recipeById: RecipeById, week: WeekPlan | undefined = plansByWeek[weekStart]): GroceryPlacement[] {
  const out: GroceryPlacement[] = [];
  if (!week) return out;
  const withWeek = plansByWeek[weekStart] === week ? plansByWeek : { ...plansByWeek, [weekStart]: week };
  for (const day of DAYS) {
    for (const slot of MEAL_SLOTS) {
      for (const [dishType, id] of Object.entries(normalizeDishes(week.days?.[day]?.[slot]) as Record<string, string>)) {
        if (!id || id === DINING_OUT || week.leftovers?.[mealKey(day, slot, dishType)]) continue;
        const ref = { weekStart, day, mealSlot: slot, dishType: dishType as DishType };
        out.push({ recipeId: id, factor: scaleFactorFor(recipeById(id), plannedServings(withWeek, ref)), day, slot });
      }
    }
  }
  for (const list of ["snacks", "preserves", "medicinal"] as const) (week[list] || []).forEach((e) => out.push({ recipeId: e.recipeId, factor: 1, list }));
  return out;
}

// ------------------------------------------------------- choosing a source ---

const REACH_DAYS = 7; // how far back leftovers look (decided 2026-10-03)

export interface SourceCandidate extends MealRef {
  recipeId: string;
  date: Date;
}

// Cooked dishes (not leftovers, not dining out) a leftovers slot could draw
// on: earlier days within the reach, or earlier meals the same day; newest
// first. Pass `recipeId` to keep only that recipe.
export function sourceCandidates(plansByWeek: PlansByWeek, target: MealRef, recipeId?: string): SourceCandidate[] {
  const targetDate = mealDate(target.weekStart, target.day);
  const earliest = new Date(targetDate.getFullYear(), targetDate.getMonth(), targetDate.getDate() - REACH_DAYS);
  const found: SourceCandidate[] = [];
  for (const [weekStart, week] of Object.entries(plansByWeek)) {
    for (const day of DAYS) {
      const date = mealDate(weekStart, day);
      if (date < earliest || date > targetDate) continue;
      for (const slot of MEAL_SLOTS) {
        if (date.getTime() === targetDate.getTime() && slotOrder(slot) >= slotOrder(target.mealSlot)) continue;
        for (const [dishType, id] of Object.entries(normalizeDishes(week.days?.[day]?.[slot]) as Record<string, string>)) {
          if (!id || id === DINING_OUT || week.leftovers?.[mealKey(day, slot, dishType)]) continue;
          if (recipeId && id !== recipeId) continue;
          found.push({ weekStart, day, mealSlot: slot, dishType: dishType as DishType, recipeId: id, date });
        }
      }
    }
  }
  return found.sort((a, b) => b.date.getTime() - a.date.getTime() || slotOrder(b.mealSlot) - slotOrder(a.mealSlot));
}

// The link to store for leftovers drawing on `source`, as of `now`. A meal
// already past, or marked cooked (0058; it may have been cooked early), is
// never rescaled, so leftovers linked to it from then on don't count.
export function linkTo(source: MealRef, now: Date, cooked = false): LeftoverSource {
  return { ...source, scalesSource: !cooked && mealDate(source.weekStart, source.day) >= startOfDay(now) };
}

// Whether a planned dish is marked cooked.
export const isCooked = (plansByWeek: PlansByWeek, ref: MealRef) => !!plansByWeek[ref.weekStart]?.cooked?.[mealKey(ref.day, ref.mealSlot, ref.dishType)];

// -------------------------------------------- mirroring the database rules ---

// What 0053's triggers do when a cooked dish changes, applied to the loaded
// plan so the screen agrees with the database without a reload: leftovers
// drawing on it follow a new recipe, or lose the link when it is cleared or
// becomes dining out or leftovers.
export function followSourceChange(plansByWeek: PlansByWeek, source: MealRef, next: { recipeId: string; isLeftover: boolean }): PlansByWeek {
  const draws = drawsOn(plansByWeek, source);
  if (draws.length === 0) return plansByWeek;
  const detach = !next.recipeId || next.recipeId === DINING_OUT || next.isLeftover;
  const out: PlansByWeek = { ...plansByWeek };
  for (const { ref } of draws) {
    const week = out[ref.weekStart];
    const key = mealKey(ref.day, ref.mealSlot, ref.dishType);
    const leftoverSources = { ...(week.leftoverSources || {}) };
    let days = week.days;
    if (detach) {
      delete leftoverSources[key];
    } else {
      const daySlots = { ...(days[ref.day] || {}) };
      daySlots[ref.mealSlot] = { ...normalizeDishes(daySlots[ref.mealSlot]), [ref.dishType]: next.recipeId };
      days = { ...days, [ref.day]: daySlots };
    }
    out[ref.weekStart] = { ...week, days, leftoverSources };
  }
  return out;
}

// Drops every link whose source matches `isGone` -- what the foreign key's
// ON DELETE SET NULL does when those meal rows are deleted (clearing a day
// or week, or replacing a week). The leftovers keep their recipe.
export function detachWhere(plansByWeek: PlansByWeek, isGone: (source: MealRef) => boolean): PlansByWeek {
  let out = plansByWeek;
  for (const [weekStart, week] of Object.entries(plansByWeek)) {
    const entries = Object.entries(week.leftoverSources || {});
    if (!entries.some(([, link]) => isGone(link))) continue;
    out = { ...out, [weekStart]: { ...week, leftoverSources: Object.fromEntries(entries.filter(([, link]) => !isGone(link))) } };
  }
  return out;
}

// When a week is copied onto another (Start from last week), its links move
// with it: a source inside the copied week shifts by the same number of days;
// one outside it is kept only if a dish with the same recipe sits at the
// shifted place in the loaded plan, and dropped otherwise (the row stays as
// "Something older"). Links are re-dated as of `now`.
export function shiftLinks(sources: Record<string, LeftoverSource>, fromWeekStart: string, toWeekStart: string, copiedWeek: WeekPlan, plansByWeek: PlansByWeek, now: Date): Record<string, LeftoverSource> {
  const shiftDays = Math.round((new Date(`${toWeekStart}T00:00:00`).getTime() - new Date(`${fromWeekStart}T00:00:00`).getTime()) / 86400000);
  const out: Record<string, LeftoverSource> = {};
  for (const [key, link] of Object.entries(sources)) {
    const [day, slot, dish] = key.split("|");
    const recipeId = (normalizeDishes(copiedWeek.days?.[day as Day]?.[slot as MealSlot]) as Record<string, string>)[dish];
    const oldDate = mealDate(link.weekStart, link.day);
    const newDate = new Date(oldDate.getFullYear(), oldDate.getMonth(), oldDate.getDate() + shiftDays);
    const ws = new Date(`${link.weekStart}T00:00:00`);
    const newWeekStart = toISODate(new Date(ws.getFullYear(), ws.getMonth(), ws.getDate() + shiftDays));
    const moved: MealRef = { weekStart: newWeekStart, day: DAYS[(newDate.getDay() + 6) % 7], mealSlot: link.mealSlot, dishType: link.dishType };
    const targetWeek = moved.weekStart === toWeekStart ? copiedWeek : plansByWeek[moved.weekStart];
    const there = (normalizeDishes(targetWeek?.days?.[moved.day]?.[moved.mealSlot]) as Record<string, string>)[moved.dishType];
    if (there && there === recipeId && !targetWeek?.leftovers?.[mealKey(moved.day, moved.mealSlot, moved.dishType)]) out[key] = linkTo(moved, now);
  }
  return out;
}

// --------------------------------------------------------------- labels ---

const DAY_LABEL_OPTS: Intl.DateTimeFormatOptions = { weekday: "long" };

// "Thursday's dinner", "today's lunch", "last night's dinner".
export function mealLabel(ref: MealRef, now: Date): string {
  const date = mealDate(ref.weekStart, ref.day);
  const today = startOfDay(now);
  const diff = Math.round((today.getTime() - date.getTime()) / 86400000);
  if (diff === 0) return `today's ${ref.mealSlot}`;
  if (diff === 1 && ref.mealSlot === "dinner") return "last night's dinner";
  if (diff === -1) return `tomorrow's ${ref.mealSlot}`;
  return `${date.toLocaleDateString(undefined, DAY_LABEL_OPTS)}'s ${ref.mealSlot}`;
}
